/**
 * Keep a staff device's push registration current without a tap.
 *
 * A device that has already granted permission can lose its server row in
 * ways the person never sees: the service worker was replaced, the VAPID
 * key rotated, or the row was deactivated after a delivery failure. Once per
 * page load, if permission is granted and this device is not the one on
 * record locally, re-register with the server. Nothing here asks for
 * permission; that stays on the Settings button.
 *
 * Mirrors usePushNotifications (portal side) for staff sessions.
 */

import { useEffect } from 'react'
import { PUSH_ENDPOINT_KEY, saveStaffSubscription } from '../api/pushNotifications'
import { VAPID_PUBLIC_KEY, applicationServerKey, isPushSupported, needsHomeScreenInstall, subscriptionKeyMatches } from '../utils/pushNotifications'

export function useStaffPushResync(userId: string | null) {
  useEffect(() => {
    if (!userId) return
    if (!isPushSupported() || Notification.permission !== 'granted') return
    if (needsHomeScreenInstall()) return
    if (!VAPID_PUBLIC_KEY) {
      console.error('[push] VITE_VAPID_PUBLIC_KEY is not set; staff push re-sync skipped.')
      return
    }

    let cancelled = false
    const timer = setTimeout(async () => {
      try {
        const reg = await navigator.serviceWorker.ready
        let sub = await reg.pushManager.getSubscription()
        // No subscription at all means the person never turned push on here
        // (or turned it off). Re-sync only refreshes an existing one.
        if (!sub) return
        if (!subscriptionKeyMatches(sub)) {
          // Subscribed under a rotated key: the server could never reach it.
          // Permission is already granted, so re-subscribing needs no tap.
          await sub.unsubscribe()
          sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: applicationServerKey(),
          })
        }
        if (cancelled) return
        if (localStorage.getItem(PUSH_ENDPOINT_KEY) === sub.endpoint) return
        await saveStaffSubscription(sub)
      } catch (err) {
        console.error('[push] Staff re-sync failed:', err)
      }
    }, 10_000)

    return () => { cancelled = true; clearTimeout(timer) }
  }, [userId])
}
